/* =========================================================================
 * PURSUIT BOARD MOVES, ON THE WORKLIST. [Jarvis · Claude] 2026-10-26
 *
 * everything-lands-on-the-worklist: every card moved on the Pursuit board
 * shows up here for the rep who holds the record — who moved it, when (with
 * the year), from what, to what — with the NEXT step for the stage it landed
 * in and the reason. It clears when the record moves on to that step's stage
 * (lib/pursuitMove.js#movesForBook). Read from the append-only history at
 * GET /api/pursuit-moves; nothing here writes.
 *
 * Never a bare zero: loading, unreadable and "no moves yet" are three
 * different sentences.
 * ========================================================================= */
import { useEffect, useMemo } from 'react'
import { useFencedState as useState } from '../../../lib/bowlMutationContext.jsx'
import { pursuitMoves } from '../../../lib/pursuitMoveApi.js'
import { movesForBook as movesForAssigned, pursuitHistoryRead } from '../../../lib/pursuitMove.js'
import { formatDay, toDay } from '../../../lib/dateFilter.js'
import styles from './PursuitMoves.module.css'

const when = (iso) => {
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return 'time not recorded'
  const d = new Date(t)
  return `${formatDay(toDay(d))}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
}

export default function PursuitMoves({ book = [], onOpen }) {
  const [now] = useState(() => new Date())
  const [state, setState] = useState({ status: 'loading', moves: [], complete: false, error: '' })
  useEffect(() => {
    const ctl = new AbortController()
    pursuitMoves({ signal: ctl.signal })
      .then((r) => { if (!ctl.signal.aborted) setState({ status: 'ready', ...pursuitHistoryRead(r), error: '' }) })
      .catch((e) => { if (!ctl.signal.aborted) setState({ status: 'error', moves: [], complete: false, error: e?.message || 'unreadable' }) })
    return () => ctl.abort()
  }, [setState])

  const assigned = book
  const items = useMemo(() => movesForAssigned(state.moves, assigned, { now }), [state.moves, assigned, now])

  return (
    <section className={styles.wrap} aria-label="Moved on the Pursuit board" data-testid="worklist-pursuit-moves">
      <h3 className={styles.head}>
        Moved on the Pursuit board
        {state.status === 'ready' && <span className={styles.count}>{items.length.toLocaleString()}{!state.complete && ' loaded'}</span>}
      </h3>
      {state.status === 'loading' && <p className={styles.note}>Reading the move history…</p>}
      {state.status === 'error' && (
        <p className={styles.note}>The move history could not be read ({state.error}). Your moves are still saved on each record.</p>
      )}
      {state.status === 'ready' && !state.complete && <p className={styles.note}>The move history is partial. Loaded moves are shown below; this read cannot establish that no other assigned cards moved.</p>}
      {state.status === 'ready' && state.complete && !items.length && (
        <p className={styles.note}>No open follow-up from moves in the last 30 days on cards assigned to you. Move one from Opportunities → Pursuit Board and its next step lands here.</p>
      )}
      {items.length > 0 && (
        <ul className={styles.list}>
          {items.map(({ id, record, move, stage, step, stale }) => (
            <li key={id} className={styles.item}>
              <button type="button" className={styles.itemHead} onClick={() => onOpen?.({ id })}>
                <span className={styles.name}>{record?.name || `Record ${id}`}</span>
                <span className={styles.move}>
                  {move.fromKnown === false || move.from == null ? 'Moved' : `${move.from} →`} <strong>{move.to}</strong>
                </span>
                <span className={styles.meta}>by {move.byName || move.byUser || 'unstamped'} · {when(move.at)}</span>
                {stale && <span className={styles.meta}>Now at {stage}, moved again since.</span>}
              </button>
              <div className={styles.next}>
                <span className={styles.why}>{step.why}</span>
                <button type="button" className={styles.cta} onClick={() => onOpen?.({ id })}>
                  {step.label}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
